import {CdkVirtualScrollViewport} from '@angular/cdk/scrolling';
import {
	AfterViewInit,
	Component,
	ElementRef,
	EventEmitter,
	Input,
	OnInit,
	Output,
	QueryList,
	ViewChild,
	ViewChildren
} from '@angular/core';
import {MatDialog} from '@angular/material/dialog';

import {TABLE_MIN_COL_WIDTH, TableControllerService} from 'src/app/services/table-controller.service';
import {AuthService} from '../../../services/auth.service';

import {DataTableSettingsComponent} from '../data-table-settings/data-table-settings.component';
import {DetailDataTableComponent} from '../detail-data-table/detail-data-table.component';

import {Action, ActionSet} from 'src/app/models/layout';
import {ColumnSetting} from '../../../models/announcements.model';
import {PageEventType} from 'libComm';
import {
	ACTION_IDS,
	ActionEvent,
	ColumnConfig,
	DatasetConfig,
	SortConfig
} from './models/data-table.models';

export {ColumnSetting};

@Component({
	selector: 'data-table',
	templateUrl: './data-table.component.html',
	styleUrls: ['./data-table.component.scss']
})
export class DataTableComponent implements OnInit, AfterViewInit
{
	@ViewChild(CdkVirtualScrollViewport, {static: false})
	public viewPort?: CdkVirtualScrollViewport;

	@ViewChild('tableTr', {static: false})
	private tableTr?: ElementRef;

	@ViewChild('actionsTh', {static: false})
	private actionsTh?: ElementRef;

	@ViewChild('selectTh', {static: false})
	private selectTh?: ElementRef;

	@ViewChildren(DetailDataTableComponent)
	detailTables!: QueryList<DetailDataTableComponent>;

	// Fallback width used in the template while columnSize is still empty, so main-table columns
	// never collapse once the max-content header backstop is removed for wrapping
	readonly MIN_COL_WIDTH = TABLE_MIN_COL_WIDTH;

	// ==========================================
	// INPUTS (API maintained for compatibility)
	// ==========================================

	@Input() pageEvent: PageEventType = {
		pageIndex: 0,
		previousPageIndex: 0,
		loader: 50,
		size: 50
	};

	@Input() tableController!: TableControllerService;
	@Input() disabledColumns: string[] = [];
	@Input() disabledActions: Action[] = [];
	@Input() isDetail = false;
	@Input() isSortable = true;
	@Input() showColumnSettings = false;
	@Input() pageReference = '';
	@Input() pageId = '';
	@Input() apiParent = '';
	@Input() apiUrl = '';
	@Input() detailId: string = '';
	@Input() tableContainerClass: string = '';

	@Input() reloadData?: () => void;

	// ITOEM-5: added logic to handle notification button
	@Input()
	notificationOption?: any[];

	// ITOEM-5: added logic to handle notification button
	@Input()
	manualOption?: any[];

	private readonly actionsToCheck: string[] = [
		ACTION_IDS.DELETE,
		ACTION_IDS.EDIT,
		ACTION_IDS.SAVE,
		ACTION_IDS.INNER_TABLE
	];

	get datasets(): DatasetConfig | null
	{
		return this.tableController?.getDatasets() ?? null;
	}

	isHighPrio(row: any): boolean
	{
		return (this.datasets?.datas?.some(c => c.columnName === 'HIGH_PRIO') ?? false)
			&& row['HIGH_PRIO'] === 'Y';
	}

	private _actionset?: ActionSet;

	@Input()
	set actionset(value: ActionSet | undefined)
	{
		this._actionset = value;
		if (this.tableController)
		{
			this.tableController.setActionset(value ?? null);
		}
	}

	get actionset(): ActionSet | undefined
	{
		return this._actionset;
	}

	// ==========================================
	// OUTPUTS
	// ==========================================

	@Output() onActionEmit = new EventEmitter<ActionEvent>();
	@Output() onResetSort = new EventEmitter<any>();

	constructor(
		private authService: AuthService,
		public dialog: MatDialog
	)
	{
	}

	// ==========================================
	// LIFECYCLE
	// ==========================================

	ngOnInit(): void
	{
		if (this.tableController)
		{
			this.tableController.setDisabledActions(this.disabledActions);
			if (this._actionset)
			{
				this.tableController.setActionset(this._actionset);
			}
		}

		const validActionIds = this._actionset?.actions?.map(action => action.id?.toUpperCase()) || [];


		// filter the options based on user roles
		this.notificationOption = this.notificationOption?.filter(notification =>
		{
			const id = notification?.notificationId;
			return typeof id === 'string' && validActionIds.includes(id.toUpperCase());
		});

		this.manualOption = this.manualOption?.filter(option =>
		{
			const id = option?.manualActionId;
			return typeof id === 'string' && validActionIds.includes(id.toUpperCase());
		});

		// ITOEM-56: retrive any user sort order
		this.tableController.SortingUserPref(this.pageId, "init");
	}

	ngAfterViewInit(): void
	{
		this.tableController.setTableRefs(this.tableTr, this.viewPort);

		// Main tables only: override the width distribution so it subtracts the actions column
		// width and caps how much wider than its content a column may grow. Detail tables keep
		// old service method (setTableRefs) untouched.
		if (!this.isDetail)
		{
			this.tableController.calculateColumnWidth = (table: ElementRef | undefined = this.tableTr) =>
			{
				if (!table) return;

				const totalWidth = this.viewPort?.elementRef.nativeElement.offsetWidth || table.nativeElement.offsetWidth;
				const actionsWidth = this.actionsTh?.nativeElement?.offsetWidth ?? 0;
				const selectWidth = this.selectTh?.nativeElement?.offsetWidth ?? 0;
				const trWidth = totalWidth - actionsWidth - selectWidth;

				const totalSizeColumn = Object.keys(this.tableController.columnWeight)
					.map(key => this.tableController.columnWeight[key])
					.reduce((accumulator, currentValue) => accumulator + currentValue, 1);

				// caps how much wider than its content-weight a column may grow, so leftover space
				// isn't dumped entirely into one column
				const MAX_EXTRA_PX = 40;

				Object.keys(this.tableController.columnWeight).forEach(key =>
				{
					if (totalSizeColumn < trWidth)
					{
						const proportional = (trWidth / totalSizeColumn) * this.tableController.columnWeight[key];
						const capped = this.tableController.columnWeight[key] + MAX_EXTRA_PX;
						this.tableController.columnSize[key] = Math.floor(Math.min(proportional, capped));
					}
					else
					{
						this.tableController.columnSize[key] = Math.floor(this.tableController.columnWeight[key]);
					}
				});
			};
		}
	}

	// ==========================================
	// PUBLIC GETTERS (delegating to tableController)
	// ==========================================

	get visibleColumns(): ColumnConfig[]
	{
		return this.tableController.getVisibleColumns();
	}

	get showMultiselectDropdown(): boolean
	{
		return this.tableController.showMultiselectDropdown();
	}

	get showMultiselectDropdownMenu(): boolean
	{
		return this.tableController.showMultiselectDropdownMenu();
	}

	get isInEditOrInsertMode(): boolean
	{
		return this.tableController.isInEditOrInsertMode();
	}

	get showActionsColumn(): boolean
	{
		return this.tableController.showActionsColumn(this.actionsToCheck);
	}

	/**
	 * Determines if actions column should be displayed on the left side.
	 * Returns true for delivery planning pages.
	 */
	shouldShowActionsOnLeft(): boolean
	{
		return false;
	}

	hasAction(actionId: string): boolean
	{
		return this.tableController.hasAction(actionId);
	}

	isActionDisabled(actionId: string): boolean
	{
		return this.tableController.isActionDisabled(actionId);
	}


	getSortedColumnInfo(columnName: string): SortConfig | null
	{
		return this.tableController.getSortedColumnInfo(columnName);
	}

	// ==========================================
	// TRACKING FUNCTIONS
	// ==========================================

	trackByIndex(index: number): number
	{
		return index;
	}

	trackByColumnName(index: number, column: ColumnConfig): string
	{
		return column.columnName;
	}

	// ==========================================
	// ROW OPERATIONS
	// ==========================================

	editRow(index: number): void
	{
		this.tableController.editRow(index);
	}

	exitRow(index: number): void
	{
		this.tableController.exitRow(index);
		this.tableController.dataSet = [...this.tableController.dataSet];
	}

	commitEdit(index: number, action: string): void
	{
		this.tableController.rowNumber = index;
		if (!this.tableController.dataSet[index].newRow)
		{
			this.tableController.commitEdit(index);
			this.datasets?.datas?.forEach((column: ColumnConfig) =>
			{
				if (this.tableController.dataSet[index][column.columnName] !==
					this.tableController.dataSet[index].backup?.[column.columnName])
				{
					this.tableController.dataSet[index].edited = true;
				}
			});
		}
		this.onActionEmit.emit({
			action,
			rowIndex: index,
			row: this.tableController.dataSet[index]
		});
	}

	onAddClick(): void
	{
		const newRowObject = this.createEmptyRowObject();
		this.tableController.dataSet = this.tableController.onAddClick({...newRowObject});
	}

	private createEmptyRowObject(): Record<string, any>
	{
		const columns: ColumnConfig[] = this.datasets?.datas ?? [];
		return columns.reduce((obj, column) =>
		{
			obj[column.columnName] = this.getDefaultColumnValue(column);
			return obj;
		}, {} as Record<string, any>);
	}

	private getDefaultColumnValue(column: ColumnConfig): any
	{
		return column.type === 'DTY_YES_NO' ? 'N' : '';
	}

	deleteRow(index: number): void
	{
		const row = this.tableController.dataSet[index];
		this.tableController.dataSet = this.tableController.deleteRow(index);
		this.onActionEmit.emit({
			action: ACTION_IDS.DELETE_ID,
			rowIndex: index,
			row
		});
	}

	editAll(): void
	{
		this.tableController.editAll();
	}

	exitAllRow(): void
	{
		this.tableController.exitAllRow();
		this.tableController.dataSet = [...this.tableController.dataSet];
	}

	viewInnerTable(index: number): void
	{
		this.tableController.handleInnerGridVisibility(index);
	}

	// ==========================================
	// ROW STATE HELPERS
	// ==========================================

	isRowCanceled(index: number): boolean
	{
		return this.tableController.isRowCanceled(index);
	}

	// ==========================================
	// VALUE CHANGE HANDLING
	// ==========================================

	onInputChange(index: number): void
	{
		this.tableController.dataSet[index].edited = true;
	}

	onRadioButtonClick(column: ColumnConfig, index: number): void
	{
		this.tableController.dataSet[index].edited = true;
		this.tableController.dataSet[index][column.columnName] =
			!this.tableController.dataSet[index][column.columnName];

		column.radioGroup?.forEach(columnName =>
		{
			this.tableController.dataSet[index][columnName] =
				!this.tableController.dataSet[index][columnName];
		});
	}

	// ==========================================
	// SORTING
	// ==========================================

	sortColumn(columnName: string): void
	{
		const currentSort = this.tableController.sortedColumns.get(columnName);

		if (currentSort)
		{
			if (currentSort.type === 'asc')
			{
				this.tableController.sortedColumns.set(columnName, {
					order: currentSort.order,
					type: 'desc'
				});
			}
			else
			{
				this.tableController.sortedColumns.delete(columnName);
				// Reorder remaining columns
				let order = 1;
				const newMap = new Map<string, { order: number; type: 'asc' | 'desc' }>();
				this.tableController.sortedColumns.forEach((value, key) =>
				{
					newMap.set(key, {order: order++, type: value.type});
				});
				this.tableController.sortedColumns = newMap;
			}
		}
		else
		{
			const maxOrder = Math.max(0, ...Array.from(this.tableController.sortedColumns.values()).map(v => v.order));
			this.tableController.sortedColumns.set(columnName, {order: maxOrder + 1, type: 'asc'});
		}

		this.tableController.setSortedColumns(this.tableController.sortedColumns);
		//ITOEM-56 notify the table controller to store the updated sorting
		this.tableController.SortingUserPref(this.pageId, "update");

		this.tableController.onSortChange.emit({
			sorting: this.tableController.sortedColumns,
			pageEvent: this.pageEvent
		});
	}

	onClearSortClick(): void
	{
		this.tableController.sortedColumns = new Map();
		this.tableController.onSortChange.emit({
			sorting: this.tableController.sortedColumns,
			pageEvent: this.pageEvent
		});

		//ITOEM-56 notify the table controller to store the updated sorting
		this.tableController.SortingUserPref(this.pageId, "reset");
	}

	onResetSortClick(): void
	{
		this.onResetSort.emit(null);

		//ITOEM-56 notify the table controller to store the updated sorting
		this.tableController.SortingUserPref(this.pageId, "reset");
	}

	// ==========================================
	// PAGINATION
	// ==========================================

	onPageChange(pageEvent: any): void
	{
		this.tableController.onPaginationChange.emit(pageEvent);
	}

	// ==========================================
	// ACTIONS
	// ==========================================

	handleToolbarAction(action: string, index?: number): void
	{
		//Action from toolbar will be emitted and delegated to general component
		if (action.endsWith('_ALL'))
		{
			this.tableController.commitEditDetail(-1);
		}
		this.onActionEmit.emit({
			action,
			rowIndex: index ?? -1,
			row: index !== undefined ? this.tableController.dataSet[index] : undefined
		});
	}

	handleRowAction(event: { action: string; rowIndex: number; row?: any }): void
	{
		const {action, rowIndex, row} = event;

		// Easy action that can be resolved here
		// For complex task or custom action call the general component
		const simpleActions: Record<string, () => void> = {
			[ACTION_IDS.EDIT]: () => this.editRow(rowIndex),
			[ACTION_IDS.EXIT_ROW]: () => this.exitRow(rowIndex),
			[ACTION_IDS.DELETE]: () => this.deleteRow(rowIndex),
			[ACTION_IDS.INNER_TABLE]: () => this.viewInnerTable(rowIndex),
			[ACTION_IDS.SAVE]: () => this.commitEdit(rowIndex, ACTION_IDS.SAVE_ID)
		};

		const handler = simpleActions[action];
		if (handler)
		{
			handler();
			return;
		}

		// Azioni che richiedono emit con suffisso _ID
		if (this.shouldEmitWithIdSuffix(action))
		{
			this.tableController.commitEditDetail(rowIndex);
			this.onActionEmit.emit({
				action: `${action}_ID`,
				rowIndex,
				row
			});
			return;
		}

		// Azioni custom non gestite internamente
		this.onActionEmit.emit({
			action,
			rowIndex,
			row
		});
	}

	private shouldEmitWithIdSuffix(action: string): boolean
	{
		return action.startsWith('ACT_EDIT_');
	}

	handleCellClick(event: { action: string; rowIndex: number }): void
	{
		this.tableController.commitEditDetail(event.rowIndex);
		this.onActionEmit.emit({
			action: event.action + '_ID',
			rowIndex: event.rowIndex,
			row: this.tableController.dataSet[event.rowIndex]
		});
	}

	// ==========================================
	// COLUMN SETTINGS
	// ==========================================

	openColumnSettingsDialog(event: MouseEvent): void
	{
		event.stopPropagation();

		const dialogRef = this.dialog.open(DataTableSettingsComponent, {
			data: {
				dataTitle: 'COLUMN_SETTINGS',
				viewList: this.visibleColumns.map(col => ({
					id: col.id,
					label: col.columnLabel
				})),
				hideList: this.tableController.getHiddenColumns().map(col => ({
					id: col.id,
					label: col.columnLabel
				}))

			}
		});

		dialogRef.afterClosed().subscribe(result =>
		{
			if (!result) return;

			if (result.hideList)
			{
				const hideList = result.hideList.map((f: any) => f.id);
				this.datasets?.datas?.forEach((data: ColumnConfig) =>
				{
					if (hideList.includes(data.id))
					{
						data.hidden = true;
					}
				});
				this.saveColumnSettings(hideList);
			}

			if (result.viewList)
			{
				const viewList = result.viewList.map((f: any) => f.id);
				this.datasets?.datas?.forEach((data: ColumnConfig) =>
				{
					if (viewList.includes(data.id))
					{
						data.hidden = false;
					}
				});
			}
		});
	}

	private saveColumnSettings(hiddenColumns: string[]): void
	{
		const columnSetting = {hiddenColumns};
		sessionStorage.setItem(`${this.pageReference}_COLUMN_SETTINGS`, JSON.stringify(columnSetting));

		let columnSettings: Record<string, any> = {};
		const stored = sessionStorage.getItem('COLUMN_SETTINGS');
		if (stored)
		{
			columnSettings = JSON.parse(stored);
		}

		columnSettings[this.pageReference] = columnSetting;
		sessionStorage.setItem('COLUMN_SETTINGS', JSON.stringify(columnSettings));

		this.authService.setColumnSettings(JSON.stringify(columnSettings)).subscribe();
	}

	// ==========================================
	// VIRTUAL SCROLL HELPERS
	// ==========================================

	get inverseOfTranslation(): string
	{
		if (!this.viewPort) return '-0px';
		const offset = this.viewPort.getOffsetToRenderedContentStart();
		return `-${offset}px`;
	}

	// ==========================================
	// INNER TABLE RELOAD
	// ==========================================

	/**
	 * Reloads all visible inner/detail tables.
	 * Called after a row in the main grid is saved to sync the detail data.
	 */
	reloadVisibleInnerTables(): void
	{
		this.detailTables?.forEach(detailTable =>
		{
			detailTable.reloadData();
		});
	}

	getMetadataErrorCss(column: any, dataset: any): string
	{
		if (!column || !dataset || !dataset.metadataErrors)
		{
			return "";
		}
		let error = dataset.metadataErrors[column.columnName];
		if (!error)
		{
			return "";
		}
		if (error["ERROR_STATUS"] == "WARNING")
		{
			return "warning";
		}
		else
			if (error["ERROR_STATUS"] == "ERROR")
			{
				return "invalid";
			}
		return "";
	}

	/**
	 * Retrieves a descriptive error message for a table cell based on metadata errors.
	 *
	 * @param column - The column object containing the column name.
	 * @param dataset - The dataset object containing metadata errors.
	 * @returns A string combining the error action and error notice,
	 *          or `null` if no errors are present.
	 */
	getMetadataErrorTooltip(column: any, dataset: any)
	{
		if (!column || !dataset || !dataset.metadataErrors)
		{
			return null;
		}
		let error = dataset.metadataErrors[column.columnName];
		if (!error)
		{
			return null
		}
		return error["ERROR_ACTION"] + " - " + error["ERROR_NOTICE"];
	}
}
